/**
 * Manage Grid (screen 08), scoped to one grid: Publish or move back to draft,
 * Lock or Unlock, Assign square to member, Copy squares from another grid
 * (Classic, merge or replace), Randomize numbers, the Winners list with Add
 * and Remove, and Clear selections. The server refuses anything the grid's
 * state does not allow; this sheet only hides what can obviously not apply.
 */
import {
  useSquaresCustomGridRename,
  useSquaresGridClear,
  useSquaresGridCopyFrom,
  useSquaresGridLock,
  useSquaresGridPublish,
  useSquaresGridRandomize,
  useSquaresGridUnlock,
  useSquaresGridUnpublish,
  useSquaresWinnerRemove,
} from "@sportsmet/contest-sdk/private";
import {
  Button,
  confirmDestructive,
  Dialog,
  Input,
  Radio,
  Select,
  Switch,
} from "@sportsmet/ui";
import { type ReactNode, useEffect, useState } from "react";

import {
  runSquaresMutation,
  useRefreshSquaresPoolLine,
} from "./squaresActions";
import {
  SQUARES_ADD_WINNER,
  SQUARES_CLEAR_CONFIRM_BODY,
  SQUARES_CLEAR_CONFIRM_LABEL,
  SQUARES_CLEAR_CONFIRM_TITLE,
  SQUARES_CUSTOM_GRID_NAME_MAX_LENGTH,
  SQUARES_CUSTOM_GRID_AXIS_MAX_LENGTH,
  SQUARES_CUSTOM_GRID_EDIT,
  SQUARES_CUSTOM_GRID_NAME_LABEL,
  SQUARES_CUSTOM_GRID_RENAME_SAVE,
  SQUARES_CUSTOM_GRID_SIDE_LABEL,
  SQUARES_CUSTOM_GRID_TOP_LABEL,
  SQUARES_MANAGE_ASSIGN,
  SQUARES_MANAGE_ASSIGN_HINT,
  SQUARES_MANAGE_CLEAR,
  SQUARES_MANAGE_CLEAR_HINT,
  SQUARES_MANAGE_CLOSE,
  SQUARES_MANAGE_COPY,
  SQUARES_MANAGE_COPY_MERGE,
  SQUARES_MANAGE_COPY_MODE,
  SQUARES_MANAGE_COPY_REPLACE,
  SQUARES_MANAGE_COPY_SOURCE,
  SQUARES_MANAGE_COPY_SUBMIT,
  SQUARES_MANAGE_LOCK,
  SQUARES_MANAGE_NO_WINNERS,
  SQUARES_MANAGE_NOTIFY,
  SQUARES_MANAGE_PUBLISH,
  SQUARES_MANAGE_PUBLISH_HINT,
  SQUARES_MANAGE_RANDOMIZE,
  SQUARES_MANAGE_RANDOMIZE_HINT,
  SQUARES_MANAGE_REMOVE_WINNER,
  SQUARES_MANAGE_TITLE,
  SQUARES_MANAGE_UNLOCK,
  SQUARES_MANAGE_UNLOCK_HINT,
  SQUARES_MANAGE_UNPUBLISH,
  SQUARES_MANAGE_UNPUBLISH_BLOCKED,
  SQUARES_MANAGE_WINNERS,
  SQUARES_REMOVE_WINNER_CONFIRM_BODY,
  SQUARES_REMOVE_WINNER_CONFIRM_LABEL,
  SQUARES_REMOVE_WINNER_CONFIRM_TITLE,
  SQUARES_WINNING_UNCLAIMED,
  squaresCopyResult,
  squaresManageLockHint,
  squaresRemoveWinnerName,
} from "./squaresCopy";
import type {
  typeSquaresGrid,
  typeSquaresGridSummary,
  typeSquaresWinner,
} from "./squaresTypes";

export type typeSquaresManageGridSheetProps = {
  open: boolean;
  onClose: () => void;
  poolId: string;
  grid: typeSquaresGrid;
  /** Other grids in the season, for Copy squares (Classic only). */
  otherGrids: readonly typeSquaresGridSummary[];
  winners: readonly typeSquaresWinner[];
  /** "Sun, Oct 20 · 1:00 PM", or null when the grid has no lock time. */
  lockLabel: string | null;
  onAddWinner: () => void;
  onStartAssign: () => void;
};

type typeBusyKey =
  | "publish"
  | "unpublish"
  | "lock"
  | "unlock"
  | "copy"
  | "randomize"
  | "clear"
  | "rename"
  | `remove:${string}`;

const ActionRow = (props: {
  title: string;
  hint?: string | undefined;
  children: ReactNode;
  testId: string;
}): ReactNode => (
  <section
    className="flex flex-col gap-2 border-b border-hairline pb-4 last:border-b-0 last:pb-0"
    data-testid={props.testId}
  >
    <h3 className="text-body-strong text-ink">{props.title}</h3>
    {props.hint === undefined ? null : (
      <p className="text-caption text-ink-secondary">{props.hint}</p>
    )}
    {props.children}
  </section>
);

export const SquaresManageGridSheet = (
  props: typeSquaresManageGridSheetProps,
): ReactNode => {
  const { grid } = props;
  const [busy, setBusy] = useState<typeBusyKey | null>(null);
  const [doNotify, setDoNotify] = useState(true);
  const [copySource, setCopySource] = useState<string | null>(null);
  const [copyMode, setCopyMode] = useState<"merge" | "replace">("merge");
  const [copyNote, setCopyNote] = useState<string | null>(null);
  // ISSUE-1282. A custom game's name and its two side names, editable here
  // like v1's grid title.
  const customTitle = grid.customTitle ?? null;
  const savedTop = grid.axes.topLabel;
  const savedSide = grid.axes.sideLabel;
  const [renameDraft, setRenameDraft] = useState(customTitle ?? "");
  const [topDraft, setTopDraft] = useState(savedTop);
  const [sideDraft, setSideDraft] = useState(savedSide);
  useEffect(() => {
    if (!props.open) return;
    setRenameDraft(customTitle ?? "");
    setTopDraft(savedTop);
    setSideDraft(savedSide);
  }, [props.open, customTitle, savedTop, savedSide]);
  const rename = useSquaresCustomGridRename();
  const isEditBlank =
    renameDraft.trim() === "" ||
    topDraft.trim() === "" ||
    sideDraft.trim() === "";
  const isEditUnchanged =
    renameDraft.trim() === customTitle &&
    topDraft.trim() === savedTop &&
    sideDraft.trim() === savedSide;

  const publish = useSquaresGridPublish();
  const unpublish = useSquaresGridUnpublish();
  const lock = useSquaresGridLock();
  const unlock = useSquaresGridUnlock();
  const copyFrom = useSquaresGridCopyFrom();
  const randomize = useSquaresGridRandomize();
  const clear = useSquaresGridClear();
  const removeWinner = useSquaresWinnerRemove();
  const refreshPoolLine = useRefreshSquaresPoolLine(props.poolId);

  const base = { poolId: props.poolId, gridId: grid.gridId };
  const isDraft = grid.publishStatus === "draft";
  const isOpen = grid.gridState === "open";
  const isStarted =
    grid.gridState === "live" ||
    grid.gridState === "final" ||
    grid.gridState === "void";

  async function run<TResult>(
    key: typeBusyKey,
    call: () => Promise<TResult>,
  ): Promise<TResult | null> {
    setBusy(key);
    const result = await runSquaresMutation(call, refreshPoolLine);
    setBusy(null);
    return result;
  }

  return (
    <Dialog
      open={props.open}
      onOpenChange={(open) => {
        if (!open) props.onClose();
      }}
      title={SQUARES_MANAGE_TITLE}
      description={
        customTitle === null ? grid.label : `${grid.label} · ${customTitle}`
      }
      autoFocus={false}
      footer={
        <Button fullWidth variant="secondary" onClick={props.onClose}>
          {SQUARES_MANAGE_CLOSE}
        </Button>
      }
    >
      <div className="flex flex-col gap-4" data-testid="squares-manage-sheet">
        {customTitle === null ? null : (
          <ActionRow
            title={SQUARES_CUSTOM_GRID_EDIT}
            testId="squares-manage-rename"
          >
            <Input
              label={SQUARES_CUSTOM_GRID_NAME_LABEL}
              maxLength={SQUARES_CUSTOM_GRID_NAME_MAX_LENGTH}
              value={renameDraft}
              data-testid="squares-manage-rename-input"
              onChange={(event) => {
                setRenameDraft(event.target.value);
              }}
            />
            <Input
              label={SQUARES_CUSTOM_GRID_TOP_LABEL}
              maxLength={SQUARES_CUSTOM_GRID_AXIS_MAX_LENGTH}
              value={topDraft}
              data-testid="squares-manage-rename-top"
              onChange={(event) => {
                setTopDraft(event.target.value);
              }}
            />
            <Input
              label={SQUARES_CUSTOM_GRID_SIDE_LABEL}
              maxLength={SQUARES_CUSTOM_GRID_AXIS_MAX_LENGTH}
              value={sideDraft}
              data-testid="squares-manage-rename-side"
              onChange={(event) => {
                setSideDraft(event.target.value);
              }}
            />
            <Button
              variant="secondary"
              disabled={isEditBlank || isEditUnchanged}
              loading={busy === "rename"}
              data-testid="squares-manage-rename-button"
              onClick={() => {
                void run("rename", () =>
                  rename.mutateAsync({
                    ...base,
                    title: renameDraft.trim(),
                    topLabel: topDraft.trim(),
                    sideLabel: sideDraft.trim(),
                  }),
                );
              }}
            >
              {SQUARES_CUSTOM_GRID_RENAME_SAVE}
            </Button>
          </ActionRow>
        )}
        {isDraft ? (
          <ActionRow
            title={SQUARES_MANAGE_PUBLISH}
            hint={SQUARES_MANAGE_PUBLISH_HINT}
            testId="squares-manage-publish"
          >
            <Switch
              label={SQUARES_MANAGE_NOTIFY}
              checked={doNotify}
              onCheckedChange={setDoNotify}
            />
            <Button
              loading={busy === "publish"}
              data-testid="squares-manage-publish-button"
              onClick={() => {
                void run("publish", () =>
                  publish.mutateAsync({ ...base, doNotifyMembers: doNotify }),
                );
              }}
            >
              {SQUARES_MANAGE_PUBLISH}
            </Button>
          </ActionRow>
        ) : isStarted ? null : (
          <ActionRow
            title={SQUARES_MANAGE_UNPUBLISH}
            hint={
              grid.adminActions.canUnpublish
                ? undefined
                : SQUARES_MANAGE_UNPUBLISH_BLOCKED
            }
            testId="squares-manage-unpublish"
          >
            <Button
              variant="secondary"
              disabled={!grid.adminActions.canUnpublish}
              data-testid="squares-manage-unpublish-button"
              loading={busy === "unpublish"}
              onClick={() => {
                void run("unpublish", () => unpublish.mutateAsync(base));
              }}
            >
              {SQUARES_MANAGE_UNPUBLISH}
            </Button>
          </ActionRow>
        )}

        {isOpen ? (
          <ActionRow
            title={SQUARES_MANAGE_LOCK}
            hint={squaresManageLockHint(props.lockLabel)}
            testId="squares-manage-lock"
          >
            <Button
              variant="secondary"
              loading={busy === "lock"}
              data-testid="squares-manage-lock-button"
              onClick={() => {
                void run("lock", () => lock.mutateAsync(base));
              }}
            >
              {SQUARES_MANAGE_LOCK}
            </Button>
          </ActionRow>
        ) : grid.gridState === "locked" ? (
          <ActionRow
            title={SQUARES_MANAGE_UNLOCK}
            hint={SQUARES_MANAGE_UNLOCK_HINT}
            testId="squares-manage-unlock"
          >
            <Button
              variant="secondary"
              disabled={!grid.adminActions.canUnlock}
              loading={busy === "unlock"}
              data-testid="squares-manage-unlock-button"
              onClick={() => {
                void run("unlock", () => unlock.mutateAsync(base));
              }}
            >
              {SQUARES_MANAGE_UNLOCK}
            </Button>
          </ActionRow>
        ) : null}

        {isOpen ? (
          <ActionRow
            title={SQUARES_MANAGE_ASSIGN}
            hint={SQUARES_MANAGE_ASSIGN_HINT}
            testId="squares-manage-assign"
          >
            <Button
              variant="secondary"
              data-testid="squares-manage-assign-button"
              onClick={props.onStartAssign}
            >
              {SQUARES_MANAGE_ASSIGN}
            </Button>
          </ActionRow>
        ) : null}

        {grid.gridType === "classic" &&
        isOpen &&
        props.otherGrids.length > 0 ? (
          <ActionRow title={SQUARES_MANAGE_COPY} testId="squares-manage-copy">
            <Select
              label={SQUARES_MANAGE_COPY_SOURCE}
              value={copySource ?? undefined}
              items={props.otherGrids.map((other) => ({
                value: other.gridId,
                label: other.label,
              }))}
              onValueChange={setCopySource}
            />
            <Radio
              label={SQUARES_MANAGE_COPY_MODE}
              value={copyMode}
              orientation="vertical"
              items={[
                { value: "merge", label: SQUARES_MANAGE_COPY_MERGE },
                { value: "replace", label: SQUARES_MANAGE_COPY_REPLACE },
              ]}
              onValueChange={(value) => {
                setCopyMode(value === "replace" ? "replace" : "merge");
              }}
            />
            <Button
              variant="secondary"
              disabled={copySource === null}
              loading={busy === "copy"}
              data-testid="squares-manage-copy-button"
              onClick={() => {
                void (async () => {
                  if (copySource === null) return;
                  const result = await run("copy", () =>
                    copyFrom.mutateAsync({
                      ...base,
                      sourceGridId: copySource,
                      mode: copyMode,
                    }),
                  );
                  const data = result?.data ?? null;
                  if (data !== null) {
                    setCopyNote(
                      squaresCopyResult({
                        copiedCount: data.copiedCount,
                        skippedCount: data.skippedCount,
                      }),
                    );
                  }
                })();
              }}
            >
              {SQUARES_MANAGE_COPY_SUBMIT}
            </Button>
            {copyNote === null ? null : (
              <p role="status" className="text-caption text-ink-secondary">
                {copyNote}
              </p>
            )}
          </ActionRow>
        ) : null}

        <ActionRow
          title={SQUARES_MANAGE_RANDOMIZE}
          hint={SQUARES_MANAGE_RANDOMIZE_HINT}
          testId="squares-manage-randomize"
        >
          <Button
            variant="secondary"
            disabled={!grid.adminActions.canRandomize}
            loading={busy === "randomize"}
            data-testid="squares-manage-randomize-button"
            onClick={() => {
              void run("randomize", () => randomize.mutateAsync(base));
            }}
          >
            {SQUARES_MANAGE_RANDOMIZE}
          </Button>
        </ActionRow>

        <ActionRow
          title={SQUARES_MANAGE_WINNERS}
          testId="squares-manage-winners"
        >
          {props.winners.length === 0 ? (
            <p className="text-body text-ink-secondary">
              {SQUARES_MANAGE_NO_WINNERS}
            </p>
          ) : (
            <ul className="flex flex-col divide-y divide-hairline">
              {props.winners.map((winner) => (
                <li
                  key={winner.winnerId}
                  className="flex items-center justify-between gap-3 py-2"
                >
                  <span className="flex min-w-0 flex-col">
                    <span className="text-caption text-ink-secondary">
                      {winner.label}
                    </span>
                    <span className="truncate text-body text-ink">
                      {winner.displayName ?? SQUARES_WINNING_UNCLAIMED}
                    </span>
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    loading={busy === `remove:${winner.winnerId}`}
                    aria-label={squaresRemoveWinnerName({
                      label: winner.label,
                      holder: winner.displayName ?? SQUARES_WINNING_UNCLAIMED,
                    })}
                    data-testid={`squares-remove-winner-${winner.winnerId}`}
                    onClick={() => {
                      void (async () => {
                        const isConfirmed = await confirmDestructive({
                          title: SQUARES_REMOVE_WINNER_CONFIRM_TITLE,
                          body: SQUARES_REMOVE_WINNER_CONFIRM_BODY,
                          confirmLabel: SQUARES_REMOVE_WINNER_CONFIRM_LABEL,
                        });
                        if (!isConfirmed) return;
                        await run(`remove:${winner.winnerId}`, () =>
                          removeWinner.mutateAsync({
                            poolId: props.poolId,
                            winnerId: winner.winnerId,
                          }),
                        );
                      })();
                    }}
                  >
                    {SQUARES_MANAGE_REMOVE_WINNER}
                  </Button>
                </li>
              ))}
            </ul>
          )}
          <Button
            variant="ghost"
            size="sm"
            className="self-start"
            data-testid="squares-manage-add-winner"
            onClick={props.onAddWinner}
          >
            {SQUARES_ADD_WINNER}
          </Button>
        </ActionRow>

        <ActionRow
          title={SQUARES_MANAGE_CLEAR}
          hint={SQUARES_MANAGE_CLEAR_HINT}
          testId="squares-manage-clear"
        >
          <Button
            variant="destructive"
            disabled={!grid.adminActions.canClear}
            loading={busy === "clear"}
            data-testid="squares-manage-clear-button"
            onClick={() => {
              void (async () => {
                const isConfirmed = await confirmDestructive({
                  title: SQUARES_CLEAR_CONFIRM_TITLE,
                  body: SQUARES_CLEAR_CONFIRM_BODY,
                  confirmLabel: SQUARES_CLEAR_CONFIRM_LABEL,
                });
                if (!isConfirmed) return;
                await run("clear", () => clear.mutateAsync(base));
              })();
            }}
          >
            {SQUARES_MANAGE_CLEAR}
          </Button>
        </ActionRow>
      </div>
    </Dialog>
  );
};
